import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchUsers, assignRole, removeRole, generateInviteLink } from '../api/rolesApi';
import { getRoleSuggestions, removeRoleSuggestion, toSuggestStatus } from '../api/roleSuggestionsApi';
import { getMemberDisplay, AliasTag, MemberNameText, NickBadge } from './memberDisplay';
import { useToast } from './Toast';
import { Button } from './UI';

// ─── Role labels ──────────────────────────────────────────────────────────────
const ROLE_LABELS = {
  EDITOR: { text: 'Редактор', color: '#2563eb', bg: '#dbeafe' },
  ATTENDANCE: { text: 'Посещаемость', color: '#7c3aed', bg: '#ede9fe' },
};

const LIMIT_OPTIONS = [
  { value: 'SINGLE_USE', label: 'Одноразовая', hint: 'Ссылкой сможет воспользоваться один человек' },
  { value: 'UNLIMITED',  label: 'Безлимитно',  hint: 'Ссылкой может воспользоваться несколько человек' },
];

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconSearch = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

const IconLink = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
  </svg>
);

const IconTrash = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/>
    <path d="M9 6V4h6v2"/>
  </svg>
);

const IconCopy = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const IconShare = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
  </svg>
);

const IconClose = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const IconPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);


const IconUserPlus = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/>
    <line x1="19" y1="8" x2="19" y2="14"/>
    <line x1="22" y1="11" x2="16" y2="11"/>
  </svg>
);

const IconWarning = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

// ─── Confirm Dialog ───────────────────────────────────────────────────────────
const ConfirmDialog = ({ message, onConfirm, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }}>
    <div style={{
      background: '#fff', borderRadius: 16, padding: '24px 20px',
      width: '100%', maxWidth: 320,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
    }}>
      <p style={{ margin: '0 0 20px', fontSize: 15, color: '#111827', lineHeight: 1.5 }}>{message}</p>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="secondary" onClick={onCancel} style={{ flex: 1 }}>Отмена</Button>
        <Button variant="danger" onClick={onConfirm} style={{ flex: 1 }}>Удалить</Button>
      </div>
    </div>
  </div>
);

// ─── Save First Dialog (for invite link in create mode) ───────────────────────
const SaveFirstDialog = ({ onSaveAndContinue, onCancel }) => (
  <div style={{
    position: 'fixed', inset: 0, zIndex: 1000,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: 24,
  }}>
    <div style={{
      background: '#fff', borderRadius: 16, padding: '24px 20px',
      width: '100%', maxWidth: 340,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
      animation: 'popIn 0.2s cubic-bezier(0.175,0.885,0.32,1.275)',
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: '50%',
        background: '#fef3c7',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 14px',
      }}>
        <IconWarning />
      </div>
      <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700, color: '#111827', textAlign: 'center' }}>
        Сначала сохраните событие
      </h3>
      <p style={{ margin: '0 0 20px', fontSize: 13, color: '#6b7280', textAlign: 'center', lineHeight: 1.6 }}>
        Чтобы сгенерировать ссылку-приглашение, событие нужно создать. Сохранить сейчас?
      </p>
      <div style={{ display: 'flex', gap: 10 }}>
        <Button variant="secondary" onClick={onCancel} style={{ flex: 1 }}>Отмена</Button>
        <Button onClick={onSaveAndContinue} style={{ flex: 1 }}>Сохранить</Button>
      </div>
    </div>
    <style>{`@keyframes popIn { from { transform: scale(0.85); opacity: 0; } to { transform: scale(1); opacity: 1; } }`}</style>
  </div>
);

// ─── Invite Link Modal ────────────────────────────────────────────────────────
const InviteLinkModal = ({ eventId, userId, hash, onClose, onReturnToChat }) => {
  const { addToast } = useToast();
  const [role, setRole] = useState('ATTENDANCE');
  const [limitType, setLimitType] = useState('SINGLE_USE');
  const [loading, setLoading] = useState(false);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const res = await generateInviteLink({
        resourceType: 'EVENT',
        resourceId: eventId,
        role,
        limitType,
        maxUsages: null,
      }, userId, hash);
      setLink(res.deepLink);
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      addToast('Ссылка скопирована', 'success');
    } catch {
      addToast('Не удалось скопировать', 'error');
    }
  };

  const handleShare = () => {
    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.openTelegramLink(
        `https://t.me/share/url?url=${encodeURIComponent(link)}`
      );
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '20px 20px 0 0',
        padding: '24px 20px 32px',
        width: '100%',
        boxShadow: '0 -4px 32px rgba(0,0,0,0.15)',
        animation: 'slideUp 0.25s ease',
      }}>
        <style>{`@keyframes slideUp { from { transform: translateY(40px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Назначить роль по ссылке</h3>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex' }}>
            <IconClose />
          </button>
        </div>

        {!link ? (
          <>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Роль</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ value: 'ATTENDANCE', label: 'Посещаемость' }].map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setRole(opt.value)}
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: role === opt.value ? '#6366f1' : '#f3f4f6',
                      color: role === opt.value ? '#fff' : '#374151',
                      border: role === opt.value ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Количество использований</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                {LIMIT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setLimitType(opt.value)}
                    style={{
                      flex: 1, padding: '9px 12px', borderRadius: 10, fontSize: 13, fontWeight: 600,
                      cursor: 'pointer', transition: 'all 0.15s',
                      background: limitType === opt.value ? '#6366f1' : '#f3f4f6',
                      color: limitType === opt.value ? '#fff' : '#374151',
                      border: limitType === opt.value ? '1.5px solid #6366f1' : '1.5px solid #e5e7eb',
                    }}
                  >{opt.label}</button>
                ))}
              </div>
              {LIMIT_OPTIONS.find(o => o.value === limitType) && (
                <p style={{ margin: 0, fontSize: 12, color: '#6b7280', lineHeight: 1.4 }}>
                  {LIMIT_OPTIONS.find(o => o.value === limitType).hint}
                </p>
              )}
            </div>

            <Button onClick={handleGenerate} loading={loading} fullWidth>
              <IconLink /> Сгенерировать ссылку
            </Button>
          </>
        ) : (
          <>
            <div style={{
              background: '#f8fafc', borderRadius: 12, padding: '12px 14px',
              marginBottom: 16, border: '1.5px solid #e5e7eb',
              fontSize: 13, color: '#374151', wordBreak: 'break-all', lineHeight: 1.5,
            }}>
              {link}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button onClick={handleCopy} variant={copied ? 'secondary' : 'primary'} style={{ flex: 1 }}>
                <IconCopy /> {copied ? 'Скопировано!' : 'Копировать'}
              </Button>
              {window.Telegram?.WebApp && (
                <Button onClick={handleShare} variant="ghost" style={{ flex: 1 }}>
                  <IconShare /> Поделиться
                </Button>
              )}
            </div>
            <button
              onClick={() => setLink(null)}
              style={{
                marginTop: 12, width: '100%', background: 'none', border: 'none',
                fontSize: 13, color: '#6b7280', cursor: 'pointer', padding: '6px 0',
              }}
            >
              Создать другую ссылку
            </button>
            {onReturnToChat && (
              <button
                onClick={onReturnToChat}
                style={{
                  marginTop: 4, width: '100%',
                  padding: '11px 16px',
                  borderRadius: 12, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
                Вернуться в чат для публикации
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// ─── User Search Row ──────────────────────────────────────────────────────────
// Поиск ролей идёт только по таблице users (includeGuests=false), но отображение
// имени/ника/алиаса переиспользует общий слой memberDisplay (как в добавлении участников).
const UserSearchRow = ({ user, onAdd, adding }) => {
  const view = getMemberDisplay(user);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 14px', background: '#fff',
      borderRadius: 12, border: '1.5px solid #f3f4f6',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: '50%',
        background: 'linear-gradient(135deg, #6366f1, #4f46e5)',
        color: '#fff', fontSize: 14, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {(view.name || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <MemberNameText view={view} />
          <AliasTag text={view.aliasTag} />
        </div>
        {view.nick && (
          <div style={{ marginTop: 1 }}>
            <NickBadge view={view} />
          </div>
        )}
      </div>
      <button
        onClick={() => onAdd(user)}
        disabled={adding}
        style={{
          background: '#6366f1', color: '#fff', border: 'none',
          borderRadius: 8, padding: '6px 10px', cursor: adding ? 'wait' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 4,
          fontSize: 12, fontWeight: 600, flexShrink: 0,
          opacity: adding ? 0.6 : 1, transition: 'opacity 0.15s',
        }}
      >
        <IconPlus /> Добавить
      </button>
    </div>
  );
};

const openTelegramChat = (username) => {
  if (!username) return;
  if (window.Telegram?.WebApp?.openTelegramLink) {
    window.Telegram.WebApp.openTelegramLink(`https://t.me/${username}`);
  }
};

// ─── Assigned Role Row ────────────────────────────────────────────────────────
const AssignedRoleRow = ({ entry, onRemove, isPending }) => {
  const rl = ROLE_LABELS[entry.role] || { text: entry.role, color: '#6b7280', bg: '#f3f4f6' };
  const view = getMemberDisplay(entry);
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '8px 12px', background: isPending ? '#fffbeb' : '#fff',
      borderRadius: 12, border: `1.5px solid ${isPending ? '#fde68a' : '#f3f4f6'}`,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: '50%',
        background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
        color: '#fff', fontSize: 13, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {(view.name || '?')[0].toUpperCase()}
      </div>
      <div
        style={{ flex: 1, minWidth: 0, cursor: view.nick ? 'pointer' : 'default' }}
        onClick={() => openTelegramChat(view.nick)}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <MemberNameText view={view} style={{ fontSize: 13, color: view.nick ? '#6366f1' : '#111827' }} />
          <AliasTag text={view.aliasTag} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
          <NickBadge view={view} style={{ opacity: 0.7 }} />
          <span style={{
            fontSize: 10, fontWeight: 700, color: rl.color,
            background: rl.bg, borderRadius: 5, padding: '1px 6px', flexShrink: 0,
          }}>{rl.text}</span>
        </div>
      </div>
      <button
        onClick={() => onRemove(entry)}
        style={{
          background: '#fee2e2', color: '#dc2626', border: 'none',
          borderRadius: 8, padding: '6px 8px', cursor: 'pointer',
          display: 'flex', alignItems: 'center', flexShrink: 0,
          transition: 'background 0.15s',
        }}
      >
        <IconTrash />
      </button>
    </div>
  );
};

// ─── Suggested Role Row ───────────────────────────────────────────────────────
// Рендерится как компактный чип, два в ряд
const SuggestedRoleChip = ({ suggestion, onAccept, onDismiss, accepting, dismissing }) => {
  const view = getMemberDisplay(suggestion.user || {});
  const busy = accepting || dismissing;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 6,
      padding: '6px 8px 6px 6px',
      background: busy ? '#f3f4f6' : '#f8fafc',
      borderRadius: 10,
      border: '1.5px dashed #d1d5db',
      opacity: busy ? 0.5 : 1,
      transition: 'opacity 0.15s, background 0.15s',
      cursor: busy ? 'wait' : 'pointer',
      flex: '1 1 0', minWidth: 0, maxWidth: 'calc(50% - 3px)',
    }}
    onClick={() => !busy && onAccept(suggestion)}
    >
      <div style={{
        width: 26, height: 26, borderRadius: '50%',
        background: 'linear-gradient(135deg, #d1d5db, #9ca3af)',
        color: '#fff', fontSize: 11, fontWeight: 700,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {(view.name || '?')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
          <MemberNameText view={view} style={{ fontSize: 12, color: '#374151' }} />
          <AliasTag text={view.aliasTag} style={{ fontSize: 9 }} />
        </div>
        {view.nick && (
          <div style={{ marginTop: 1 }}>
            <NickBadge view={view} style={{ fontSize: 10, color: '#9ca3af', opacity: 1 }} />
          </div>
        )}
      </div>
      {/* Dismiss button — если нет реального id, просто убираем с экрана без API */}
      <button
        onClick={(e) => { e.stopPropagation(); if (!busy) onDismiss(suggestion); }}
        disabled={busy}
        title="Убрать"
        style={{
          background: 'none', border: 'none', cursor: busy ? 'wait' : 'pointer',
          color: '#9ca3af', padding: 2, display: 'flex', flexShrink: 0,
          borderRadius: 4,
        }}
      >
        <IconClose />
      </button>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
// Props:
//   event          — объект события (нужен для просмотра: event.id, event.organizationId, event.assignedRoles)
//   createMode     — bool: true если вызывается со страницы создания (событие ещё не сохранено)
//   organizationId — нужен в createMode (не из event)
//   onPendingRolesChange(roles) — callback для передачи pending-ролей в CreateEventPage
//   onSaveFirst()  — callback: пользователь согласился сохранить событие, чтобы получить ссылку
const RolesPanel = ({ event, currentUserId, createMode = false, organizationId, userId, hash, onPendingRolesChange, onSaveFirst, autoOpenInviteModal = false, onReturnToChat }) => {
  const { addToast } = useToast();

  const orgId = createMode ? organizationId : event?.organizationId;
  const eventId = createMode ? null : event?.id;

  // В режиме просмотра — роли с бэкенда, в режиме создания — pending (локальные)
  const [roles, setRoles] = useState(createMode ? [] : (event?.assignedRoles || []));
  const [confirmDelete, setConfirmDelete] = useState(null);

  // ── Suggestions ──
  // suggestions — список {id, user, mode} где mode = AUTO_ADD | SUGGEST
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  // { [suggestionId]: 'accepting' | 'dismissing' }
  const [suggestionBusy, setSuggestionBusy] = useState({});

  // Загружаем suggestions при монтировании
  useEffect(() => {
    setSuggestionsLoading(true);
    getRoleSuggestions(userId, hash)
      .then((data) => {
        const list = Array.isArray(data) ? data : [];

        // Получаем уже назначенные userId из roles (чтобы не дублировать)
        const existingUserIds = new Set(
          (createMode ? [] : (event?.assignedRoles || [])).map(r => r.userId)
        );

        // AUTO_ADD — предзаполняем роли (только в createMode)
        if (createMode) {
          const autoAdd = list.filter(
            s => s.mode === 'AUTO_ADD' && !existingUserIds.has(s.user?.userId)
          );
          if (autoAdd.length > 0) {
            setRoles(prev => {
              const next = [...prev];
              for (const s of autoAdd) {
                const alreadyIn = next.some(r => r.userId === s.user?.userId && r.role === (s.user?.role || 'ATTENDANCE'));
                if (!alreadyIn) {
                  next.push({
                    userId: s.user?.userId,
                    telegramUserName: s.user?.telegramUserName,
                    fullName: s.user?.fullName,
                    alias: s.user?.alias,
                    role: s.user?.role || 'ATTENDANCE',
                    suggestionId: s.id,
                  });
                }
              }
              return next;
            });
          }
        }

        // SUGGEST — показываем как советуемые (фильтруем уже назначенных)
        // В editMode все показываем как SUGGEST независимо от mode
        const filteredSuggest = list.filter(s => {
          const targetMode = createMode ? s.mode === 'SUGGEST' : true;
          return targetMode && !existingUserIds.has(s.user?.userId);
        });
        setSuggestions(filteredSuggest);
      })
      .catch((err) => {
        // Не показываем ошибку пользователю — suggestions некритичны
        console.warn('Failed to load role suggestions:', err);
      })
      .finally(() => setSuggestionsLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // После изменения roles — убираем из suggestions тех, кого уже добавили
  useEffect(() => {
    if (suggestions.length === 0) return;
    const assignedIds = new Set(roles.map(r => r.userId));
    setSuggestions(prev => prev.filter(s => !assignedIds.has(s.user?.userId)));
  }, [roles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Принять советуемую роль
  const handleAcceptSuggestion = async (suggestion) => {
    const user = suggestion.user;
    if (!user) return;
    setSuggestionBusy(prev => ({ ...prev, [suggestion.id]: 'accepting' }));
    try {
      if (createMode) {
        // Локально добавляем в pending роли
        setRoles(prev => {
          const exists = prev.some(r => r.userId === user.userId && r.role === (user.role || 'ATTENDANCE'));
          if (exists) return prev;
          return [...prev, {
            userId: user.userId,
            telegramUserName: user.telegramUserName,
            fullName: user.fullName,
            alias: user.alias,
            role: user.role || 'ATTENDANCE',
          }];
        });
      } else {
        // В режиме просмотра — сразу назначаем через API
        await assignRole({
          targetUserId: user.userId,
          role: user.role || 'ATTENDANCE',
          resourceType: 'EVENT',
          resourceId: eventId,
        }, userId, hash);
        setRoles(prev => {
          const exists = prev.some(r => r.userId === user.userId && r.role === (user.role || 'ATTENDANCE'));
          if (exists) return prev;
          return [...prev, {
            userId: user.userId,
            telegramUserName: user.telegramUserName,
            fullName: user.fullName,
            alias: user.alias,
            role: user.role || 'ATTENDANCE',
          }];
        });
        addToast(`${user.fullName || user.telegramUserName} добавлен`, 'success');
      }
      // Убираем из suggestions
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setSuggestionBusy(prev => { const n = { ...prev }; delete n[suggestion.id]; return n; });
    }
  };

  // Убрать советуемую роль
  const handleDismissSuggestion = async (suggestion) => {
    setSuggestionBusy(prev => ({ ...prev, [suggestion.id]: 'dismissing' }));
    try {
      if (suggestion.id != null) {
        await removeRoleSuggestion(suggestion.id, userId, hash);
      }
      setSuggestions(prev => prev.filter(s => s !== suggestion));
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setSuggestionBusy(prev => { const n = { ...prev }; delete n[suggestion.id]; return n; });
    }
  };

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showSaveFirstDialog, setShowSaveFirstDialog] = useState(false);

  // Автоматически открыть InviteLinkModal при маунте (после saveFirst flow)
  useEffect(() => {
    if (autoOpenInviteModal) {
      setShowInviteModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Search state
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [searchPage, setSearchPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [addingUserId, setAddingUserId] = useState(null);

  const searchDebounce = useRef(null);
  const queryRef = useRef('');

  // Propagate pending roles to parent
  useEffect(() => {
    if (createMode && onPendingRolesChange) {
      onPendingRolesChange(roles);
    }
  }, [roles, createMode, onPendingRolesChange]);

  // Search
  const doSearch = useCallback(async (q, page, append = false) => {
    if (!q.trim()) {
      setSearchResults([]);
      setHasMore(false);
      setSearchDone(false);
      return;
    }
    if (page === 0) setSearching(true);
    else setLoadingMore(true);

    try {
      const res = await searchUsers(q, orgId, page, 15, userId, hash);
      setSearchResults(prev => append ? [...prev, ...res.content] : res.content);
      setHasMore(!res.last);
      setSearchPage(page);
      setSearchDone(true);
    } catch (err) {
      addToast(`Ошибка поиска: ${err.message}`, 'error');
      setSearchDone(true);
    } finally {
      setSearching(false);
      setLoadingMore(false);
    }
  }, [orgId, userId, hash, addToast]);

  const handleQueryChange = (e) => {
    const q = e.target.value;
    setQuery(q);
    queryRef.current = q;
    setSearchPage(0);
    setSearchDone(false);
    clearTimeout(searchDebounce.current);
    if (!q.trim()) {
      setSearchResults([]);
      return;
    }
    searchDebounce.current = setTimeout(() => doSearch(q, 0, false), 350);
  };

  // Подгрузка следующей страницы по кнопке (единообразно с добавлением участников).
  const handleLoadMore = () => {
    if (loadingMore || !hasMore) return;
    doSearch(queryRef.current, searchPage + 1, true);
  };

  // Add role
  const handleAdd = async (user) => {
    setAddingUserId(user.id);
    try {
      if (createMode) {
        // В режиме создания — просто запоминаем локально
        const alreadyExists = roles.some(r => r.userId === user.id && r.role === 'ATTENDANCE');
        if (alreadyExists) {
          addToast('Пользователь уже добавлен', 'info');
          return;
        }
        setRoles(prev => [...prev, {
          userId: user.id,
          telegramUserName: user.telegramUserName,
          fullName: user.fullName,
          alias: user.alias,
          role: 'ATTENDANCE',
        }]);
        addToast(`${user.fullName || user.telegramUserName} добавлен`, 'success');
        setQuery('');
        setSearchResults([]);
        setSearchDone(false);
      } else {
        await assignRole({
          targetUserId: user.id,
          role: 'ATTENDANCE',
          resourceType: 'EVENT',
          resourceId: eventId,
        }, userId, hash);
        setRoles(prev => {
          const exists = prev.some(r => r.userId === user.id && r.role === 'ATTENDANCE');
          if (exists) return prev;
          return [...prev, {
            userId: user.id,
            telegramUserName: user.telegramUserName,
            fullName: user.fullName,
            alias: user.alias,
            role: 'ATTENDANCE',
          }];
        });
        addToast(`${user.fullName || user.telegramUserName} добавлен`, 'success');
      }
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setAddingUserId(null);
    }
  };

  // Remove role
  const handleRemoveConfirm = async () => {
    if (!confirmDelete) return;
    try {
      if (!createMode) {
        await removeRole({
          targetUserId: confirmDelete.userId,
          role: confirmDelete.role,
          resourceType: 'EVENT',
          resourceId: eventId,
        }, userId, hash);
      } else if (confirmDelete.suggestionId != null) {
        await toSuggestStatus(confirmDelete.suggestionId, userId, hash);
      }

      const updatedRoles = roles.filter(r => !(r.userId === confirmDelete.userId && r.role === confirmDelete.role));
      setRoles(updatedRoles);
      addToast('Роль удалена', 'success');

      if (!createMode) {
        // Бэкенд создал новый suggestion — перезапрашиваем чтобы получить реальный id
        try {
          const fresh = await getRoleSuggestions(userId, hash);
          const freshList = Array.isArray(fresh) ? fresh : [];
          const assignedIds = new Set(updatedRoles.map(r => r.userId));
          setSuggestions(freshList.filter(s => !assignedIds.has(s.user?.userId)));
        } catch {
          // Fallback — добавляем локально без id (крестик просто уберёт с экрана)
          setSuggestions(prev => {
            const alreadyIn = prev.some(s => s.user?.userId === confirmDelete.userId);
            if (alreadyIn) return prev;
            return [...prev, {
              id: null,
              user: {
                userId: confirmDelete.userId,
                telegramUserName: confirmDelete.telegramUserName,
                fullName: confirmDelete.fullName,
                alias: confirmDelete.alias,
                role: confirmDelete.role,
              },
              mode: 'SUGGEST',
            }];
          });
        }
      } else {
        // createMode — добавляем локально с сохранённым suggestionId
        setSuggestions(prev => {
          const alreadyIn = prev.some(s => s.user?.userId === confirmDelete.userId);
          if (alreadyIn) return prev;
          return [...prev, {
            id: confirmDelete.suggestionId ?? null,
            user: {
              userId: confirmDelete.userId,
              telegramUserName: confirmDelete.telegramUserName,
              fullName: confirmDelete.fullName,
              role: confirmDelete.role,
            },
            mode: 'SUGGEST',
          }];
        });
      }
    } catch (err) {
      addToast(`Ошибка: ${err.message}`, 'error');
    } finally {
      setConfirmDelete(null);
    }
  };

  // Invite button click
  const handleInviteClick = () => {
    if (createMode) {
      setShowSaveFirstDialog(true);
    } else {
      setShowInviteModal(true);
    }
  };

  // User agreed to save first
  const handleSaveFirst = () => {
    setShowSaveFirstDialog(false);
    onSaveFirst?.();
  };

  const noResults = searchDone && !searching && searchResults.length === 0 && query.trim();

  return (
    <>
      <div style={{
        background: '#fff', borderRadius: 14, padding: '14px 16px',
        boxShadow: createMode ? 'none' : '0 1px 8px rgba(0,0,0,0.05)',
        marginBottom: createMode ? 0 : 14,
      }}>

        {/* Assigned roles list */}
        {roles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
            {roles.map((entry, i) => (
              <AssignedRoleRow
                key={`${entry.userId}-${entry.role}-${i}`}
                entry={entry}
                isPending={createMode}
                onRemove={setConfirmDelete}
              />
            ))}
          </div>
        )}

        {/* Suggested roles block */}
        {suggestions.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 8,
            }}>
              <IconUserPlus />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Советуемые
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {suggestions.map(suggestion => (
                <SuggestedRoleChip
                  key={suggestion.id}
                  suggestion={suggestion}
                  onAccept={handleAcceptSuggestion}
                  onDismiss={handleDismissSuggestion}
                  accepting={suggestionBusy[suggestion.id] === 'accepting'}
                  dismissing={suggestionBusy[suggestion.id] === 'dismissing'}
                />
              ))}
            </div>
          </div>
        )}

        {suggestionsLoading && (
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10, textAlign: 'center' }}>
            Загрузка...
          </div>
        )}

        {/* Search */}
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <span style={{
            position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            color: '#9ca3af', display: 'flex',
          }}>
            <IconSearch />
          </span>
          <input
            value={query}
            onChange={handleQueryChange}
            placeholder="Найти пользователя..."
            style={{
              width: '100%', padding: '10px 12px 10px 36px',
              borderRadius: 10, border: '1.5px solid #e5e7eb',
              fontSize: 14, color: '#111827', background: '#f8fafc',
              outline: 'none', boxSizing: 'border-box',
            }}
            onFocus={e => e.target.style.borderColor = '#6366f1'}
            onBlur={e => e.target.style.borderColor = '#e5e7eb'}
          />
        </div>

        {/* Search results */}
        {searching && (
          <div style={{ textAlign: 'center', padding: '14px 0', color: '#9ca3af', fontSize: 14 }}>
            Поиск...
          </div>
        )}

        {searchResults.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
            {searchResults.map(user => (
              <UserSearchRow
                key={user.id}
                user={user}
                onAdd={handleAdd}
                adding={addingUserId === user.id}
              />
            ))}
            {hasMore && (
              <button
                onClick={handleLoadMore}
                disabled={loadingMore}
                style={{
                  width: '100%', padding: '9px 12px',
                  borderRadius: 10, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#6366f1',
                  fontSize: 13, fontWeight: 600,
                  cursor: loadingMore ? 'wait' : 'pointer',
                }}
              >
                {loadingMore ? 'Загрузка...' : 'Загрузить ещё'}
              </button>
            )}
          </div>
        )}

        {/* No results */}
        {noResults && (
          <div style={{
            padding: '10px 14px', borderRadius: 10,
            background: '#f8fafc', border: '1.5px solid #e5e7eb',
            marginBottom: 10,
          }}>
            <p style={{ margin: '0 0 4px', fontSize: 13, color: '#6b7280', lineHeight: 1.5 }}>
              С вашими событиями пока не контактировали люди с таким именем.
            </p>
            <p style={{ margin: 0, fontSize: 12, color: '#9ca3af' }}>
              Воспользуйтесь ссылкой-приглашением ниже, чтобы добавить участника.
            </p>
          </div>
        )}

        {/* Invite link button — below search and results */}
        <button
          onClick={handleInviteClick}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 12,
            padding: '11px 14px',
            background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)',
            border: '1.5px dashed #a78bfa',
            borderRadius: 12, cursor: 'pointer', transition: 'opacity 0.15s',
          }}
        >
          <div style={{
            width: 36, height: 36, borderRadius: '50%',
            background: 'linear-gradient(135deg, #8b5cf6, #6d28d9)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <IconLink />
          </div>
          <div style={{ textAlign: 'left' }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#5b21b6' }}>Назначить роль по ссылке</div>
            <div style={{ fontSize: 12, color: '#7c3aed' }}>Сгенерировать ссылку-приглашение</div>
          </div>
        </button>
      </div>

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <ConfirmDialog
          message={`Удалить роль «${ROLE_LABELS[confirmDelete.role]?.text || confirmDelete.role}» у пользователя ${confirmDelete.fullName || confirmDelete.telegramUserName}?`}
          onConfirm={handleRemoveConfirm}
          onCancel={() => setConfirmDelete(null)}
        />
      )}

      {/* Save first dialog (create mode) */}
      {showSaveFirstDialog && (
        <SaveFirstDialog
          onSaveAndContinue={handleSaveFirst}
          onCancel={() => setShowSaveFirstDialog(false)}
        />
      )}

      {/* Invite modal (view mode) */}
      {showInviteModal && (
        <InviteLinkModal
          eventId={eventId}
          userId={userId}
          hash={hash}
          onClose={() => setShowInviteModal(false)}
          onReturnToChat={onReturnToChat}
        />
      )}
    </>
  );
};

export default RolesPanel;